import {
    AlertTriangle,
    ArrowRight,
    Briefcase,
    ChevronLeft,
    CircleDollarSign,
    Info,
    LoaderCircle,
    Sparkles,
    Tag,
} from 'lucide-react'
import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { toast } from 'react-toastify'
import { BASE_URL_API } from '../../References/URLs'
import ActiveStoreContext from '../../store/ActiveStoreContext'
import AuthContext from '../../store/auth-context'
import styles from './ReviseStaffSalaryScreen.module.css'

const parseActiveStore = (rawStore) => {
    try {
        return rawStore && rawStore.trim() !== '' ? JSON.parse(rawStore) : null
    } catch {
        return null
    }
}

const fmtAmount = (value) => `₹${Number(value || 0).toLocaleString('en-IN')}`

const fmtDate = (value) => {
    if (!value) return '—'
    const date = new Date(value)
    if (Number.isNaN(date.getTime())) return value
    return date.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })
}

const formatComponentValue = (component, basicAmountInput) => {
    const nameOrCode = `${component?.componentName || ''} ${component?.componentCode || ''}`.toLowerCase()
    if (nameOrCode.includes('basic')) {
        return basicAmountInput && Number(basicAmountInput) > 0
            ? fmtAmount(Number(basicAmountInput))
            : '₹[basicAmount entered]'
    }

    if (component?.valueDisplay) return component.valueDisplay

    if (component?.valueType === 'FLAT') return fmtAmount(component?.value)
    if (component?.valueType === 'PERCENT_OF_BASIC') return `${component?.value}% of Basic`
    if (component?.valueType === 'PERCENT_OF_GROSS') return `${component?.value}% of Gross`
    return 'Auto-computed'
}

const ReviseStaffSalaryScreen = () => {
    const navigate = useNavigate()
    const location = useLocation()
    const authCtx = useContext(AuthContext)
    const asCtx = useContext(ActiveStoreContext)
    const activeStore = parseActiveStore(asCtx?.activeStore)
    const storeIdentifier = activeStore?.storeIdentifier || ''

    const {
        staffIdentifier,
        staffName,
        currentSalaryAssignment,
    } = location.state || {}

    const [formData, setFormData] = useState({
        templateIdentifier: '',
        basicAmount: currentSalaryAssignment?.basicAmount?.toString() || '',
        effectiveFrom: new Date().toISOString().split('T')[0],
        assignmentRemarks: '',
        revisionReason: '',
    })
    const [selectedTemplate, setSelectedTemplate] = useState(null)
    const [templateOptions, setTemplateOptions] = useState([])
    const [errors, setErrors] = useState({})
    const [isSaving, setIsSaving] = useState(false)
    const [isLoadingTemplates, setIsLoadingTemplates] = useState(false)
    const [isLoadingTemplateDetail, setIsLoadingTemplateDetail] = useState(false)

    const setField = (field, value) => {
        setFormData((prev) => ({ ...prev, [field]: value }))
        if (errors[field]) {
            setErrors((prev) => ({ ...prev, [field]: undefined }))
        }
    }

    const fetchTemplateOptions = useCallback(async () => {
        if (!authCtx?.token || !storeIdentifier) return

        try {
            setIsLoadingTemplates(true)
            const response = await fetch(BASE_URL_API + 'getStoreSalaryStructureList', {
                method: 'POST',
                body: JSON.stringify({
                    token: authCtx.token,
                    storeIdentifier,
                    showArchived: false,
                }),
            })

            if (!response.ok) return

            const respo = await response.json()
            if (respo?.status === 'success') {
                const templates = Array.isArray(respo?.response?.templates)
                    ? respo.response.templates
                    : []
                setTemplateOptions(templates)
                return
            }

            if (respo?.message === 'tokenExpired') {
                authCtx.logout()
            }
        } catch (error) {
            console.error('getStoreSalaryStructureList error:', error)
        } finally {
            setIsLoadingTemplates(false)
        }
    }, [authCtx, storeIdentifier])

    const fetchTemplateDetail = useCallback(async (templateIdentifier) => {
        if (!authCtx?.token || !storeIdentifier || !templateIdentifier) return

        try {
            setIsLoadingTemplateDetail(true)
            const response = await fetch(BASE_URL_API + 'getSalaryStructureTemplateDetail', {
                method: 'POST',
                body: JSON.stringify({
                    token: authCtx.token,
                    storeIdentifier,
                    templateIdentifier,
                }),
            })

            if (!response.ok) {
                setSelectedTemplate(null)
                return
            }

            const respo = await response.json()
            if (respo?.status === 'success') {
                setSelectedTemplate(respo?.response?.template || null)
                return
            }

            if (respo?.message === 'tokenExpired') {
                authCtx.logout()
                return
            }

            setSelectedTemplate(null)
        } catch (error) {
            console.error('getSalaryStructureTemplateDetail error:', error)
            setSelectedTemplate(null)
        } finally {
            setIsLoadingTemplateDetail(false)
        }
    }, [authCtx, storeIdentifier])

    useEffect(() => {
        fetchTemplateOptions()
    }, [fetchTemplateOptions])

    useEffect(() => {
        if (!formData.templateIdentifier) {
            setSelectedTemplate(null)
            return
        }
        fetchTemplateDetail(formData.templateIdentifier)
    }, [fetchTemplateDetail, formData.templateIdentifier])

    const templatePreviewComponents = useMemo(
        () => Array.isArray(selectedTemplate?.components?.active) ? selectedTemplate.components.active : [],
        [selectedTemplate]
    )

    const selectedTemplateMeta = useMemo(
        () => templateOptions.find((entry) => entry.templateIdentifier === formData.templateIdentifier) || null,
        [templateOptions, formData.templateIdentifier]
    )

    const getChangePreview = useCallback(() => {
        if (!currentSalaryAssignment) return null

        const currentBasic = Number(currentSalaryAssignment?.basicAmount || 0)
        const newBasic = parseFloat(formData.basicAmount) || 0
        const newTemplate = formData.templateIdentifier

        const basicChanged = Math.abs(newBasic - currentBasic) > 0.001
        const templateChanged = Boolean(newTemplate) && newTemplate !== currentSalaryAssignment?.templateIdentifier

        if (!basicChanged && !templateChanged) return null

        if (templateChanged && basicChanged) {
            return `Changing template + basic ${fmtAmount(currentBasic)} → ${fmtAmount(newBasic)}`
        }

        if (basicChanged) {
            const diff = newBasic - currentBasic
            const pct = currentBasic > 0 ? ((Math.abs(diff) / currentBasic) * 100).toFixed(1) : '0.0'
            const dir = diff >= 0 ? '▲' : '▼'
            return `${dir} Basic ${fmtAmount(currentBasic)} → ${fmtAmount(newBasic)} (${pct}%)`
        }

        if (templateChanged) {
            return `Template: "${currentSalaryAssignment?.templateName || 'Current'}" → "${selectedTemplateMeta?.templateName || selectedTemplate?.templateName || 'Selected'}"`
        }

        return null
    }, [currentSalaryAssignment, formData.basicAmount, formData.templateIdentifier, selectedTemplateMeta, selectedTemplate])

    const changePreview = getChangePreview()

    const reviseStaffSalary = async () => {
        if (!authCtx?.token || !storeIdentifier || !staffIdentifier) return

        const newErrors = {}
        if (!formData.basicAmount || parseFloat(formData.basicAmount) <= 0) {
            newErrors.basicAmount = 'Enter a valid basic salary amount.'
        }
        if (!formData.effectiveFrom) {
            newErrors.effectiveFrom = 'Select an effective date.'
        }
        if (!formData.assignmentRemarks.trim()) {
            newErrors.assignmentRemarks = 'Provide a brief note for this revision.'
        }

        if (Object.keys(newErrors).length > 0) {
            setErrors(newErrors)
            return
        }

        if (!changePreview) {
            toast.info('No changes to save.')
            return
        }

        setErrors({})

        try {
            setIsSaving(true)

            const payload = {
                token: authCtx.token,
                storeIdentifier,
                staffIdentifier,
                basicAmount: parseFloat(formData.basicAmount),
                effectiveFrom: formData.effectiveFrom,
                assignedBy: authCtx?.staffIdentifier || authCtx?.authIdentifier || '',
                assignmentRemarks: formData.assignmentRemarks.trim(),
                revisionReason: formData.revisionReason.trim(),
            }

            if (formData.templateIdentifier) {
                payload.templateIdentifier = formData.templateIdentifier
            }

            const response = await fetch(BASE_URL_API + 'reviseStaffSalary', {
                method: 'POST',
                body: JSON.stringify(payload),
            })

            if (!response.ok) {
                toast.error('Something went wrong. Please try again.')
                return
            }

            const respo = await response.json()

            if (respo?.status === 'success') {
                toast.success(respo?.message || 'Salary revised successfully.')
                navigate(-1)
                return
            }

            if (respo?.message === 'tokenExpired') {
                authCtx.logout()
                return
            }

            switch (respo?.errorCode) {
                case 'PAY_005':
                    toast.info('No salary assigned yet. Use "Assign Salary Structure" instead.')
                    navigate('/payroll/assign-salary-structure', {
                        replace: true,
                        state: { staffIdentifier, staffName },
                    })
                    break

                case 'VAL_008':
                    if ((respo?.message || '').includes('No changes detected')) {
                        toast.info('No changes to save.')
                    } else {
                        setErrors((prev) => ({ ...prev, templateIdentifier: respo?.message || 'Invalid template selected.' }))
                    }
                    break

                case 'VAL_006':
                    setErrors((prev) => ({
                        ...prev,
                        effectiveFrom: `Must be after ${respo?.response?.currentAssignmentEffectiveFrom || 'current assignment start date'}.`,
                    }))
                    break

                case 'VAL_001':
                    if ((respo?.message || '').includes('basicAmount')) {
                        setErrors((prev) => ({ ...prev, basicAmount: respo.message }))
                    } else if ((respo?.message || '').includes('assignmentRemarks')) {
                        setErrors((prev) => ({ ...prev, assignmentRemarks: respo.message }))
                    } else {
                        toast.error(respo?.message || 'Missing required fields.')
                    }
                    break

                case 'VAL_005':
                    setErrors((prev) => ({ ...prev, effectiveFrom: 'Invalid date.' }))
                    break

                case 'COMP_004':
                    setErrors((prev) => ({ ...prev, templateIdentifier: 'Template not found.' }))
                    break

                default:
                    toast.error(respo?.message || 'Failed to save revision.')
                    break
            }
        } catch (error) {
            console.error('reviseStaffSalary error:', error)
            toast.error('Network error. Please check your connection.')
        } finally {
            setIsSaving(false)
        }
    }

    if (!currentSalaryAssignment) {
        return (
            <div className={styles.page}>
                <header className={styles.header}>
                    <button type="button" className={styles.backBtn} onClick={() => navigate(-1)}>
                        <ChevronLeft size={20} />
                    </button>
                    <div className={styles.headerMeta}>
                        <span className={styles.heroPill}><Sparkles size={12} /> Payroll</span>
                        <h1 className={styles.headerTitle}>Revise Salary</h1>
                    </div>
                </header>
                <section className={styles.alertCard}>
                    <AlertTriangle size={14} />
                    <div>
                        <strong>No current salary assignment found.</strong>
                        <p>Open this screen from staff profile / assignment screen where current assignment is available.</p>
                    </div>
                </section>
            </div>
        )
    }

    return (
        <div className={styles.page}>
            <header className={styles.header}>
                <button type="button" className={styles.backBtn} onClick={() => navigate(-1)}>
                    <ChevronLeft size={20} />
                </button>
                <div className={styles.headerMeta}>
                    <span className={styles.heroPill}><Sparkles size={12} /> Payroll</span>
                    <h1 className={styles.headerTitle}>Revise Salary</h1>
                    <p className={styles.headerSub}>for {staffName || 'Staff member'}</p>
                </div>
            </header>

            <div className={styles.formWrap}>
                {!storeIdentifier && (
                    <section className={styles.alertCard}>
                        <AlertTriangle size={14} />
                        <div>
                            <strong>Select an active store to continue.</strong>
                            <p>Salary revision is store-specific.</p>
                        </div>
                    </section>
                )}

                <section className={styles.currentCard}>
                    <div className={styles.currentHead}><CircleDollarSign size={15} /> Current Assignment</div>
                    <p className={styles.currentLine}><span>Currently on:</span> {currentSalaryAssignment?.templateName || '—'}</p>
                    <p className={styles.currentLine}><span>Basic:</span> {fmtAmount(currentSalaryAssignment?.basicAmount)}/month</p>
                    <p className={styles.currentLine}><span>Active since:</span> {fmtDate(currentSalaryAssignment?.effectiveFrom)}</p>
                </section>

                {changePreview && (
                    <section className={styles.previewBanner}>
                        <Info size={14} />
                        <p>{changePreview}</p>
                    </section>
                )}

                <section className={styles.formCard}>
                    <label className={styles.fieldLabel} htmlFor="templateIdentifier">Change Salary Structure</label>
                    <select
                        id="templateIdentifier"
                        className={`${styles.selectInput} ${errors.templateIdentifier ? styles.inputError : ''}`}
                        value={formData.templateIdentifier}
                        onChange={(event) => setField('templateIdentifier', event.target.value)}
                    >
                        <option value="">{isLoadingTemplates ? 'Loading salary structures…' : `Keep current: ${currentSalaryAssignment?.templateName || 'Current'}`}</option>
                        {templateOptions.map((template) => (
                            <option key={template.templateIdentifier} value={template.templateIdentifier}>
                                {template.templateName}{template.designationLabel ? ` — ${template.designationLabel}` : ''}
                            </option>
                        ))}
                    </select>
                    <p className={styles.fieldHint}>Leave blank to keep current: {currentSalaryAssignment?.templateName || 'Current template'}.</p>
                    {errors.templateIdentifier && <p className={styles.fieldError}>{errors.templateIdentifier}</p>}

                    {formData.templateIdentifier && selectedTemplateMeta?.designationLabel && (
                        <div className={styles.templateQuickMeta}>
                            <span className={styles.metaChip}><Tag size={11} /> {selectedTemplateMeta.designationLabel}</span>
                        </div>
                    )}
                </section>

                {!!formData.templateIdentifier && (
                    <section className={styles.previewCard}>
                        <div className={styles.previewHeader}>
                            <Briefcase size={14} /> New Template Preview
                            {isLoadingTemplateDetail && <LoaderCircle size={13} className={styles.spin} />}
                        </div>
                        {!isLoadingTemplateDetail && templatePreviewComponents.length === 0 && (
                            <p className={styles.emptyText}>No active components found for selected template.</p>
                        )}
                        {!isLoadingTemplateDetail && templatePreviewComponents.length > 0 && (
                            <div className={styles.previewList}>
                                {templatePreviewComponents.map((component) => (
                                    <div key={component.componentIdentifier} className={styles.previewRow}>
                                        <span className={styles.previewName}>{component.componentName}</span>
                                        <span className={styles.previewValue}>{formatComponentValue(component, formData.basicAmount)}</span>
                                    </div>
                                ))}
                            </div>
                        )}
                    </section>
                )}

                <section className={styles.formCard}>
                    <label className={styles.fieldLabel} htmlFor="basicAmount">New Basic Salary (₹) <span className={styles.required}>*</span></label>
                    <input
                        id="basicAmount"
                        className={`${styles.textInput} ${errors.basicAmount ? styles.inputError : ''}`}
                        type="number"
                        min="0"
                        step="0.01"
                        value={formData.basicAmount}
                        onChange={(event) => setField('basicAmount', event.target.value)}
                        placeholder="e.g. 18000"
                    />
                    {errors.basicAmount && <p className={styles.fieldError}>{errors.basicAmount}</p>}
                </section>

                <section className={styles.formCard}>
                    <label className={styles.fieldLabel} htmlFor="effectiveFrom">Effective From <span className={styles.required}>*</span></label>
                    <input
                        id="effectiveFrom"
                        className={`${styles.textInput} ${errors.effectiveFrom ? styles.inputError : ''}`}
                        type="date"
                        value={formData.effectiveFrom}
                        onChange={(event) => setField('effectiveFrom', event.target.value)}
                    />
                    {errors.effectiveFrom && <p className={styles.fieldError}>{errors.effectiveFrom}</p>}
                </section>

                <section className={styles.formCard}>
                    <label className={styles.fieldLabel} htmlFor="assignmentRemarks">Revision Note <span className={styles.required}>*</span></label>
                    <textarea
                        id="assignmentRemarks"
                        className={`${styles.textArea} ${errors.assignmentRemarks ? styles.inputError : ''}`}
                        rows={3}
                        value={formData.assignmentRemarks}
                        onChange={(event) => setField('assignmentRemarks', event.target.value)}
                        placeholder="e.g. Annual increment, Promotion, Market correction"
                    />
                    {errors.assignmentRemarks && <p className={styles.fieldError}>{errors.assignmentRemarks}</p>}
                </section>

                <section className={styles.formCard}>
                    <label className={styles.fieldLabel} htmlFor="revisionReason">Detailed Reason (for records)</label>
                    <textarea
                        id="revisionReason"
                        className={styles.textArea}
                        rows={3}
                        value={formData.revisionReason}
                        onChange={(event) => setField('revisionReason', event.target.value)}
                        placeholder="e.g. Annual review rating 4/5, completed certification"
                    />
                </section>

                <button
                    type="button"
                    className={`${styles.saveBtn} ${(isSaving || !changePreview || !storeIdentifier) ? styles.saveBtnDisabled : ''}`}
                    onClick={reviseStaffSalary}
                    disabled={isSaving || !changePreview || !storeIdentifier}
                >
                    {isSaving
                        ? <><LoaderCircle size={16} className={styles.spin} /> Saving...</>
                        : <>Save Revision <ArrowRight size={15} /></>}
                </button>
            </div>
        </div>
    )
}

export default ReviseStaffSalaryScreen